import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Store, 
  Package, 
  Warehouse,
  Truck,
  ShoppingCart, 
  CreditCard,
  MessageSquare,
  ClipboardCheck,
  Wallet,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import logo from '@/assets/branding/favicon.png';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard' },
  {
    label: 'Sales',
    icon: ShoppingCart,
    permission: 'orders',
    children: [
      { path: '/customers', label: 'Customers' },
      { path: '/orders', label: 'Orders' },
      { path: '/payments', label: 'Payments' },
      { path: '/sales-returns', label: 'Sales Returns' },
      { path: '/credit-notes', label: 'Credit Notes' },
    ],
  },
  {
    label: 'Product Catalog',
    icon: Package,
    permission: 'products',
    children: [
      { path: '/products', label: 'Products' },
      { path: '/pricing-costing', label: 'Pricing & Costing' }
    ],
  },
  {
    label: 'Operations',
    icon: Warehouse,
    permission: 'warehouse',
    children: [
      { path: '/warehouse/overview', label: 'Warehouse Overview' },
      { path: '/warehouse/list', label: 'Warehouses' },
      { path: '/warehouse/stock-details', label: 'Stock Details' },
      { path: '/warehouse/stock-register', label: 'Stock Register' },
      { path: '/warehouse/raw-materials', label: 'Raw Materials' },
      { path: '/warehouse/raw-stock', label: 'Raw Stock' },
      { path: '/warehouse/raw-stock-audit', label: 'Raw Stock Audit' },
      { path: '/warehouse/blending', label: 'Bulk Blending' },
      { path: '/warehouse/packing-materials', label: 'Packing Materials' },
      { path: '/warehouse/packing-requests', label: 'Packing Requests' },
      { path: '/warehouse/packing-sessions', label: 'Packing Sessions' },
      { path: '/warehouse', label: 'Warehouse Settings' },
      { path: '/reports/alerts', label: 'Alerting Hub' },
    ],
  },
  {
    label: 'Procurement',
    icon: Truck,
    permission: 'purchases',
    children: [
      { path: '/vendors', label: 'Vendors' },
      { path: '/purchase-orders', label: 'Purchase Orders' },
      { path: '/vendor-payments', label: 'Vendor Payments' },
    ],
  },
  {
    label: 'Finance',
    icon: Wallet,
    permission: 'payments',
    children: [
      { path: '/financial-year-close', label: 'Financial Year Close' },
      { path: '/gst', label: 'GST Dashboard' },
      { path: '/staff-collections', label: 'Staff Collections' },
      { path: '/ledger', label: 'Ledger' },
      { path: '/vouchers', label: 'Vouchers' },
      { path: '/expenses', label: 'Expenses' },
    ],
  },
  {
    label: 'People',
    icon: Users,
    permission: 'sales_staff',
    children: [
      { path: '/sales-staff', label: 'Sales Staff' },
      { path: '/staff-activities', label: 'Staff Activities' },
      { path: '/payroll', label: 'Payroll' }
    ],
  },
  {
    label: 'Leads & CRM',
    icon: MessageSquare,
    permission: 'leads',
    children: [
      { path: '/crm', label: 'CRM' },
      { path: '/leads', label: 'Leads' },
      { path: '/lead-tasks', label: 'Lead Tasks' },
      { path: '/tasks', label: 'Tasks' }
    ],
  },
  {
    label: 'Reports',
    icon: BarChart3,
    permission: 'reports',
    children: [
      { path: '/reports/sales', label: 'Sales Overview' },
      { path: '/reports/financial', label: 'Financial Overview' },
      { path: '/reports/staff', label: 'Staff Performance' },
      { path: '/reports/products', label: 'Product Sales' },
      { path: '/reports/collections', label: 'Collections' },
      { path: '/reports/short-urls', label: 'Short URLs' },
      { path: '/reports/qr-codes', label: 'QR Codes' },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    permission: null,
    children: [
      { path: '/settings', label: 'General' },
      { path: '/security/audit-logs', label: 'Security Audit' },
      { path: '/push-notifications', label: 'Push Notifications' },
      { path: '/settings/short-urls', label: 'Short URLs' },
      { path: '/settings/qr-codes', label: 'QR Codes' },
      { path: '/settings/testimonials', label: 'Testimonials' },
      { path: '/settings/email-templates', label: 'Email Templates' },
    ],
  },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const [reportsOpen, setReportsOpen] = useState(location.pathname.startsWith('/reports'));
  const [productCatalogOpen, setProductCatalogOpen] = useState(
    location.pathname.startsWith('/products') || location.pathname.startsWith('/pricing-costing')
  );
  const [salesOpen, setSalesOpen] = useState(
    location.pathname.startsWith('/customers') ||
    location.pathname.startsWith('/orders') ||
    location.pathname.startsWith('/payments') ||
    location.pathname.startsWith('/sales-returns') ||
    location.pathname.startsWith('/credit-notes')
  );
  const [financeOpen, setFinanceOpen] = useState(
    location.pathname.startsWith('/financial-year-close') ||
    location.pathname.startsWith('/gst') ||
    location.pathname.startsWith('/staff-collections') ||
    location.pathname.startsWith('/vouchers') ||
    location.pathname.startsWith('/ledger') ||
    location.pathname.startsWith('/expenses')
  );
  const [operationsOpen, setOperationsOpen] = useState(
    location.pathname.startsWith('/warehouse') || location.pathname.startsWith('/reports/alerts')
  );
  const [procurementOpen, setProcurementOpen] = useState(
    location.pathname.startsWith('/vendors') ||
    location.pathname.startsWith('/purchase-orders') ||
    location.pathname.startsWith('/vendor-payments')
  );
  const [peopleOpen, setPeopleOpen] = useState(
    location.pathname.startsWith('/sales-staff') ||
    location.pathname.startsWith('/staff-activities') ||
    location.pathname.startsWith('/payroll')
  );
  const [leadsCrmOpen, setLeadsCrmOpen] = useState(
    location.pathname.startsWith('/crm') || location.pathname.startsWith('/leads') || location.pathname.startsWith('/lead-tasks') || location.pathname.startsWith('/tasks')
  );
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings') || location.pathname.startsWith('/push-notifications') || location.pathname.startsWith('/security/audit-logs')
  );
  const { hasPermission } = useAuth();

  const filteredNavItems = navItems.filter(item => {
    if (!item.permission) return true; // No permission required
    return hasPermission(item.permission);
  });

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-[#2D2420] text-white transition-all duration-300 z-50 flex flex-col",
        collapsed ? "w-20" : "w-64"
      )}
      style={{
        backgroundImage: 'linear-gradient(180deg, #2D2420 0%, #5C4033 100%)',
      }}
    >
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <img
              src={logo}
              alt="Agniya Spices"
              className="h-8 w-8 object-contain"
            />
          </div>
          {!collapsed && (
            <div className="transition-opacity duration-300">
              <h1 className="font-bold text-lg tracking-tight">Agniya Spices</h1>
              <p className="text-xs text-white/60">Admin Panel</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => {
            if (item.children?.length) {
              const isReports = item.label === 'Reports';
              const isProductCatalog = item.label === 'Product Catalog';
              const isSales = item.label === 'Sales';
              const isFinance = item.label === 'Finance';
              const isPeople = item.label === 'People';
              const isLeadsCrm = item.label === 'Leads & CRM';
              const isOperations = item.label === 'Operations';
              const isProcurement = item.label === 'Procurement';
              const isSettings = item.label === 'Settings';
              const isOpen = isReports
                ? reportsOpen
                : isProductCatalog
                ? productCatalogOpen
                : isSales
                ? salesOpen
                : isFinance
                ? financeOpen
                : isOperations
                ? operationsOpen
                : isProcurement
                ? procurementOpen
                : isPeople
                ? peopleOpen
                : isSettings
                ? settingsOpen
                : leadsCrmOpen;
              const toggleOpen = isReports
                ? () => setReportsOpen((prev) => !prev)
                : isProductCatalog
                ? () => setProductCatalogOpen((prev) => !prev)
                : isSales
                ? () => setSalesOpen((prev) => !prev)
                : isFinance
                ? () => setFinanceOpen((prev) => !prev)
                : isOperations
                ? () => setOperationsOpen((prev) => !prev)
                : isProcurement
                ? () => setProcurementOpen((prev) => !prev)
                : isPeople
                ? () => setPeopleOpen((prev) => !prev)
                : isSettings
                ? () => setSettingsOpen((prev) => !prev)
                : () => setLeadsCrmOpen((prev) => !prev);
              if (collapsed) {
                return (
                  <li key={item.label}>
                    <NavLink
                      to={item.children[0].path}
                      data-testid="nav-reports"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                          "hover:bg-white/10",
                          isActive
                            ? "bg-[#C97A5E] text-white shadow-lg"
                            : "text-white/70 hover:text-white"
                        )
                      }
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                    </NavLink>
                  </li>
                );
              }

              return (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={toggleOpen}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-left",
                      "hover:bg-white/10",
                      isOpen ? "text-white" : "text-white/70 hover:text-white"
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-toggle`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium text-sm">{item.label}</span>
                    <span className="ml-auto">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </span>
                  </button>
                  {isOpen && (
                    <ul className="space-y-1 pl-8">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <NavLink
                            to={child.path}
                            end={child.path === '/warehouse' || child.path === '/settings'}
                            data-testid={`nav-${child.label.toLowerCase().replace(' ', '-')}`}
                            className={({ isActive }) =>
                              cn(
                                "flex items-center gap-3 px-4 py-2 rounded-xl transition-all duration-200",
                                "hover:bg-white/10",
                                isActive
                                  ? "bg-[#C97A5E] text-white shadow-lg"
                                  : "text-white/70 hover:text-white"
                              )
                            }
                          >
                            <span className="font-medium text-sm">{child.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                      "hover:bg-white/10",
                      isActive
                        ? "bg-[#C97A5E] text-white shadow-lg"
                        : "text-white/70 hover:text-white"
                    )
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.label}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-[#C97A5E] rounded-full flex items-center justify-center text-white shadow-lg hover:bg-[#B06850] transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Grain Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")"
        }}
      />
    </aside>
  );
};
